<template>
  <div v-if="phase === 'running'" class="progress mb-3" style="height: 20px">
    <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar"
      :aria-valuenow="progress" aria-valuemin="0" :aria-valuemax="currentTotal"
      :style="{ width: progressPercentage }">
      {{ `${progress}/${currentTotal}` }}
    </div>
  </div>
  <div v-else class="alert alert-secondary d-flex align-items-center gap-2 mb-3">
    <span class="spinner-border spinner-border-sm" role="status"></span>
    <span>{{ phase === 'checking' ? checkingLabel : finishingLabel }}</span>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import apiClient from "@/entrypoints/frontend/apiClient";

// A job can take a moment to be picked up (or finish before we ever observe
// it as running), so we keep polling for a bounded number of attempts before
// giving up and reloading anyway -- otherwise fast jobs would never surface
// any feedback and the page would be stuck showing stale data.
const MAX_CHECKING_ATTEMPTS = 8;

export default defineComponent({
  name: "BulkStatusProgressBar",
  props: {
    url: {
      type: String,
      required: true,
    },
    initialCount: {
      type: Number,
      default: 0,
    },
    initialRunning: {
      type: Boolean,
      default: false,
    },
    total: {
      type: Number,
      default: 100,
    },
    checkingLabel: {
      type: String,
      required: true,
    },
    finishingLabel: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      phase: (this.initialRunning ? "running" : "checking") as "checking" | "running" | "finishing",
      progress: this.initialCount,
      currentTotal: this.total,
      checkingAttempts: 0,
      intervalId: null as ReturnType<typeof setInterval> | null,
    };
  },
  computed: {
    progressPercentage(): string {
      if (this.currentTotal <= 0) return "0%";
      const pct = Math.round((this.progress / this.currentTotal) * 100);
      return `${Math.min(100, Math.max(0, pct))}%`;
    }
  },
  mounted() {
    this.checkStatus();
    this.intervalId = setInterval(this.checkStatus, 1000);
  },
  beforeUnmount() {
    if (this.intervalId) window.clearInterval(this.intervalId);
  },
  methods: {
    async checkStatus() {
      try {
        const response = await apiClient.get(this.url);

        if (response.data.running) {
          this.phase = "running";
          this.progress = response.data.progress;
          this.currentTotal = response.data.total;
          return;
        }

        if (this.phase === "running") {
          this.finish();
          return;
        }
      } catch (error) {
        // Treat a failed poll like a checking tick instead of an unhandled
        // rejection -- setInterval would otherwise keep firing it every second.
        console.error("BulkStatusProgressBar: failed to poll status", error);
      }

      this.checkingAttempts += 1;
      if (this.checkingAttempts >= MAX_CHECKING_ATTEMPTS) this.finish();
    },
    finish() {
      this.phase = "finishing";
      if (this.intervalId) window.clearInterval(this.intervalId);
      setTimeout(() => {
        // Strip bulk_status_check before navigating -- reload() would otherwise
        // re-request the same URL, remounting this widget and polling forever.
        const url = new URL(window.location.href);
        url.searchParams.delete("bulk_status_check");
        window.location.href = url.toString();
      }, 500);
    },
  },
});
</script>
